import { useMutation } from '@tanstack/react-query';
import {
  FEEDBACK_MAX_LENGTH,
  FEEDBACK_MIN_LENGTH,
  FeedbackSource,
} from '@activepieces/shared';
import { t } from 'i18next';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { showErrorToast, toast } from '@/components/ui/use-toast';
import { feedbackApi } from '../lib/feedback-api';

export type FeedbackPopoverProps = {
  source: FeedbackSource;
  children: React.ReactNode;
};

export const FeedbackPopover = ({ source, children }: FeedbackPopoverProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const { mutate: submitFeedback, isPending } = useMutation({
    mutationFn: async () => {
      return await feedbackApi.submit({
        feedback,
        source,
      });
    },
    onSuccess: () => {
      toast({
        title: t(
          'Your feedback has been successfully sent. Thank you very much!',
        ),
        variant: 'success',
        duration: 3000,
      });
      setIsPopoverOpen(false);
      setFeedback('');
      setErrorMessage('');
    },
    onError: (error) => {
      showErrorToast(error);
    },
  });

  const handleCancel = () => {
    setFeedback('');
    setErrorMessage('');
    setIsPopoverOpen(false);
  };

  const handleFeedbackChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { value } = event.target;

    if (value.length > FEEDBACK_MAX_LENGTH) return;

    setFeedback(value);

    const isTooShort = value.length > 0 && value.length < FEEDBACK_MIN_LENGTH;

    setErrorMessage(
      isTooShort
        ? t('Feedback must be at least {count} characters', {
            count: FEEDBACK_MIN_LENGTH,
          })
        : '',
    );
  };

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={(open) => {
        setIsPopoverOpen(open);
        if (!open) {
          setFeedback('');
          setErrorMessage('');
        }
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-[400px]" align="end">
        <div className="flex flex-col gap-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none">{t('Feedback Khub')}</h4>
            <p className="text-sm text-muted-foreground">
              {t(
                'Do you have any feedback to share with us about this feature?',
              )}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Textarea
              value={feedback}
              onChange={handleFeedbackChange}
              placeholder={t('Your message…')}
              className="resize-none min-h-[120px]"
              disabled={isPending}
            />
            <div className="flex justify-between items-center text-sm">
              {errorMessage && (
                <span className="text-destructive text-xs">{errorMessage}</span>
              )}
              <span className="ml-auto text-xs text-muted-foreground">
                {feedback.length}/{FEEDBACK_MAX_LENGTH}
              </span>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isPending}
            >
              {t('Cancel')}
            </Button>
            <Button
              size="sm"
              onClick={() => submitFeedback()}
              loading={isPending}
              disabled={isPending || feedback.length < FEEDBACK_MIN_LENGTH}
            >
              {t('Send')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
